import { useState, useEffect } from 'react';
import { socialAPI } from '../../services/api';
import { normalizeAvailableNiches } from '@/lib/niches';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  TailAdminEmptyState,
  TailAdminPanel,
} from '@/components/template/tailadmin/DashboardPrimitives';

const BRMSocialProfile = () => {
  const [profiles, setProfiles] = useState([]);
  const [selectedNiches, setSelectedNiches] = useState([]);
  const [availableNiches, setAvailableNiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [formData, setFormData] = useState({
    platform: 'instagram',
    username: '',
    follower_count: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profilesRes, nichesRes, availableRes] = await Promise.all([
        socialAPI.listProfiles(),
        socialAPI.getMyNiches(),
        socialAPI.getAvailableNiches(),
      ]);
      setProfiles(profilesRes.profiles || []);
      setSelectedNiches(nichesRes.niches || []);
      setAvailableNiches(normalizeAvailableNiches(availableRes));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkProfile = async (e) => {
    e.preventDefault();
    try {
      await socialAPI.linkProfile({
        platform: formData.platform,
        platform_user_id: `${formData.platform}_${Date.now()}`,
        username: formData.username,
        follower_count: parseInt(formData.follower_count) || 0,
        profile_url: `https://${formData.platform}.com/${formData.username}`
      });
      
      setShowLinkForm(false);
      setFormData({ platform: 'instagram', username: '', follower_count: '' });
      loadData();
    } catch (error) {
      console.error('Error linking profile:', error);
      alert(error.response?.data?.detail || 'Failed to link profile');
    }
  };

  const handleUnlinkProfile = async (platform) => {
    if (!window.confirm(`Unlink ${platform}?`)) return;
    
    try {
      await socialAPI.unlinkProfile(platform);
      loadData();
    } catch (error) {
      console.error('Error unlinking profile:', error);
    }
  };

  const toggleNiche = (nicheSlug) => {
    setSelectedNiches(prev => {
      if (prev.includes(nicheSlug)) {
        return prev.filter(n => n !== nicheSlug);
      } else {
        if (prev.length >= 5) {
          alert('You can select up to 5 niches');
          return prev;
        }
        return [...prev, nicheSlug];
      }
    });
  };

  const handleSaveNiches = async () => {
    if (selectedNiches.length === 0) {
      alert('Please select at least one niche');
      return;
    }

    try {
      await socialAPI.setNiches({ niches: selectedNiches.map((n) => n.toLowerCase()) });
      alert('Niches saved successfully!');
    } catch (error) {
      console.error('Error saving niches:', error);
      alert('Failed to save niches');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-6 pb-24">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Social Profiles & Niches</h1>
        <p className="text-sm text-gray-500">Link your social media accounts and select your niches to match with relevant campaigns.</p>
      </div>

      {/* Social Profiles */}
      <TailAdminPanel title="Linked Social Profiles" description="Your follower count helps brands discover you." action={null}>
        <div className="space-y-4">
          {profiles.length === 0 ? (
            <TailAdminEmptyState title="No social profiles linked yet" description="Add your social profiles so brands can understand your reach." />
          ) : (
            <div className="space-y-3">
              {profiles.map((profile) => (
                <div key={profile.profile_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-brand-500 rounded-full flex items-center justify-center text-white font-bold">
                      {profile.platform === 'instagram' && 'IG'}
                      {profile.platform === 'tiktok' && 'TT'}
                      {profile.platform === 'facebook' && 'FB'}
                    </div>
                    <div>
                      <p className="font-semibold capitalize">{profile.platform}</p>
                      <p className="text-sm text-gray-600">@{profile.username}</p>
                      <p className="text-xs text-gray-500">{profile.follower_count.toLocaleString()} followers (self-reported)</p>
                      <p className="text-xs font-medium text-amber-700">
                        {profile.is_verified ? 'Verified' : 'Unverified — not used for campaign eligibility'}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleUnlinkProfile(profile.platform)}
                  >
                    Unlink
                  </Button>
                </div>
              ))}
            </div>
          )}

          {!showLinkForm && profiles.length < 3 && (
            <Button onClick={() => setShowLinkForm(true)} className="w-full">
              + Link Social Profile
            </Button>
          )}

          {showLinkForm && (
            <form onSubmit={handleLinkProfile} className="space-y-4 p-4 bg-brand-50 rounded-lg">
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                Social OAuth is not available yet. Manual follower counts are saved as unverified and cannot unlock follower-gated campaigns.
              </p>
              <div>
                <Label>Platform</Label>
                <select
                  value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 bg-white p-2 text-sm focus:border-brand-400"
                  required
                >
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="facebook">Facebook</option>
                </select>
              </div>
              <div>
                <Label>Username</Label>
                <Input
                  placeholder="your_username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="border-gray-200 focus:border-brand-400"
                  required
                />
              </div>
              <div>
                <Label>Follower Count</Label>
                <Input
                  type="number"
                  placeholder="5000"
                  value={formData.follower_count}
                  onChange={(e) => setFormData({ ...formData, follower_count: e.target.value })}
                  className="border-gray-200 focus:border-brand-400"
                  required
                />
              </div>
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1">Link Profile</Button>
                <Button type="button" variant="outline" onClick={() => setShowLinkForm(false)}>Cancel</Button>
              </div>
            </form>
          )}
        </div>
      </TailAdminPanel>

      {/* Niche Selection */}
      <TailAdminPanel title="Your Niches" description="Choose up to 5 niches that match your content and audience." action={null}>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {availableNiches.map(({ slug, label }) => (
              <button
                type="button"
                key={slug}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                  selectedNiches.includes(slug)
                    ? 'border-brand-200 bg-brand-50 text-brand-500'
                    : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                }`}
                onClick={() => toggleNiche(slug)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="pt-4">
            <p className="text-sm text-gray-600 mb-2">
              Selected: {selectedNiches.length}/5
            </p>
            <Button onClick={handleSaveNiches} disabled={selectedNiches.length === 0}>
              Save Niches
            </Button>
          </div>
        </div>
      </TailAdminPanel>
    </div>
  );
};

export default BRMSocialProfile;
